import React, { useMemo } from "react";
import { GrDocumentPdf } from "react-icons/gr";
import { PhotographIcon } from "@heroicons/react/outline";
import { MdEmail, MdOutlineOpenInNew } from "react-icons/md";
import DOMPurify from "dompurify";
import Highlighter from "react-highlight-words";
import { dateFormaterWithTime } from "../../../helpers/formatters";
import useNav from "../../../hooks/useNav";

// CSS for highlighting in markdown content
const emailStyles = `
  .highlighted {
    background-color: rgb(254 240 138);
    color: rgb(133 77 14);
  }
`;



const Email = ({ email, query }) => {
  const nav = useNav();
  const sanitizedHtml = email.body ? DOMPurify.sanitize(email.body) : '';
  const searchWords = query && query.trim() !== '' ? (Array.isArray(query) ? query : [query]) : [];

  const renderContent = useMemo(() => {
    if (query && query.trim() !== "" && sanitizedHtml) {
      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedQuery, 'gi');
      const highlightedHtml = sanitizedHtml.replace(regex, match => `<span class="highlighted">${match}</span>`);
      return { __html: highlightedHtml };
    }
    return { __html: sanitizedHtml };
  }, [sanitizedHtml, query]);

  const handleOpenThread = () => {
    if (email.thread_id) {
      // Navigate to email thread using the bunmail route pattern
      nav(`/crm/emails/inbox/thread/${email.thread_id}`);
    }
  };

  return (
    <>
      <style>{emailStyles}</style>
      <div
        className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer mb-2"
        onClick={handleOpenThread}
      >
        {/* Header section */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center">
            <MdEmail className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              {searchWords.length > 0 && email.subject ? (
                <h3 className="font-medium text-gray-900 truncate">
                  <Highlighter
                    highlightClassName="bg-yellow-200 text-yellow-900"
                    searchWords={searchWords}
                    autoEscape={true}
                    textToHighlight={email.subject}
                  />
                </h3>
              ) : (
                <h3 className="font-medium text-gray-900 truncate">{email.subject || '(no subject)'}</h3>
              )}
            </div>
          </div>
          <div className="flex items-center ml-4">
            <MdOutlineOpenInNew className="h-4 w-4 text-gray-400" />
          </div>
        </div>

        {/* Email details */}
        <div className="flex flex-col text-sm text-gray-500 mb-3 space-y-1">
          <div className="flex justify-between">
            <div>
              {searchWords.length > 0 && email.from_name ? (
                <span>
                  From: <Highlighter
                    highlightClassName="bg-yellow-200 text-yellow-900"
                    searchWords={searchWords}
                    autoEscape={true}
                    textToHighlight={email.from_name}
                  />
                </span>
              ) : (
                <span>From: {email.from_name || email.from_email}</span>
              )}
            </div>
            <div className="text-xs text-gray-400">
              {dateFormaterWithTime(new Date(email.date))}
            </div>
          </div>
          <div>
            {searchWords.length > 0 && email.to_email ? (
              <span>
                To: <Highlighter
                  highlightClassName="bg-yellow-200 text-yellow-900"
                  searchWords={searchWords}
                  autoEscape={true}
                  textToHighlight={email.to_email}
                />
              </span>
            ) : (
              <span>To: {email.to_name || email.to_email}</span>
            )}
          </div>
        </div>

        {/* Content section */}
        <div className="text-sm text-gray-700 line-clamp-3 mb-3">
          {email.body && sanitizedHtml ? (
            <div
              className="markdown"
              dangerouslySetInnerHTML={renderContent}
            />
          ) : (
            <Highlighter
              highlightClassName="bg-yellow-200 text-yellow-900"
              searchWords={searchWords}
              autoEscape={true}
              textToHighlight={email.snippet || ''}
            />
          )}
        </div>

        {/* Attachments section */}
        {email.attachments && email.attachments.length > 0 && (
          <div className="border-t border-gray-100 pt-3">
            <p className="text-sm text-gray-600 mb-2">Attachments ({email.attachments.length})</p>
            <div className="flex space-x-3 overflow-x-auto">
              {email.attachments.map((attachment, index) => (
                <div
                  key={attachment.attachmentId || index}
                  className="flex items-center space-x-2 bg-gray-50 rounded-md p-2 min-w-0"
                  onClick={(e) => e.stopPropagation()} // Prevent opening thread when clicking attachment
                >
                  {attachment.mimeType === "application/pdf" && (
                    <GrDocumentPdf className="text-red-500 flex-shrink-0" />
                  )}
                  {attachment.mimeType && attachment.mimeType.includes("image") && (
                    <PhotographIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  )}
                  <span className="text-xs text-gray-600 truncate">
                    {attachment.filename ? attachment.filename.substring(0, 20) + (attachment.filename.length > 20 ? '...' : '') : 'Attachment'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status indicators */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-2">
            {!email.is_read && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Unread
              </span>
            )}
            {email.labels && email.labels.includes('IMPORTANT') && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Important
              </span>
            )}
          </div>
          <div className="text-xs text-gray-400">
            Thread ID: {email.thread_id}
          </div>
        </div>
      </div>
    </>
  );
};

export default Email;
